import React, { useEffect, useState } from 'react';
import { getEmployees } from '@/modules/orders/redux/OrderCRUD';
import { getMaterialRequests, getMaterialRequest } from '@/modules/inventory/requestMaterial/redux/RequestMaterialCRUD';
import { getItems } from '@/modules/inventory/items/redux/ItemCRUD';
import TextareaAutosize from 'react-textarea-autosize';
import { Box, Button, Chip, Grid, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { format, isValid, parseISO } from 'date-fns';

import { LabeledField } from '@/__core/components/LabeledField';
import { ItemSupplierAddModal } from '@/modules/inventory/items/components/modals';

import { StatusManageItem } from '@/modules/settings/redux/SettingsCRUD';
import { PurchasingSupplierOption } from '../hooks/usePurchasingSuppliers';
import { InventoryPurchaseOrderFormData, PurchaseOrderLine } from '../types';
import PurchaseOrderLinesTable from './PurchaseOrderLinesTable';
import AddButtonAutocomplete from '@/__core/hocs/AddButtonAutocomplete';
import useRequestMaterialWarehouses from '@/modules/inventory/requestMaterial/hooks/useRequestMaterialWarehouses';
import { RequestMaterialItemOption } from '@/modules/inventory/requestMaterial/types';

type AddOptionCustomDialogProps = {
  open: boolean;
  onClose: () => void;
  onSave: (data: any) => Promise<any>;
  loading?: boolean;
};

interface WarehouseSelectProps {
  companyId?: string;
  value?: string;
  warehouseName?: string;
  onChange: (warehouseId: string | null, warehouseName?: string) => void;
  disabled?: boolean;
}

function WarehouseSelect({ companyId, value, warehouseName, onChange, disabled = false }: WarehouseSelectProps) {
  const { warehouseOptions, loading } = useRequestMaterialWarehouses({ companyId: companyId || '', skip: false, onError: () => {} });

  const effectiveOptions = (warehouseOptions && warehouseOptions.length > 0)
    ? warehouseOptions
    : (value ? [{ id: value, name: warehouseName || value }] : []);

  return (
    <TextField
      select
      size='small'
      value={value || ''}
      onChange={(e) => {
        const next = e.target.value || null;
        const found = effectiveOptions.find((w) => w.id === next) as any;
        onChange(next, found ? found.name : undefined);
      }}
      disabled={disabled}
      fullWidth
      SelectProps={{ displayEmpty: true }}
    >
      {loading && <MenuItem value=''><em>Đang tải...</em></MenuItem>}
      {!loading && effectiveOptions.length === 0 && <MenuItem value=''><em>-- Chưa có kho --</em></MenuItem>}
      {!loading && effectiveOptions.map((w) => (
        <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>
      ))}
    </TextField>
  );
}

interface EmployeeSelectProps {
  value?: string;
  name?: string;
  onChange: (id: string | null, name?: string | null) => void;
  disabled?: boolean;
}

function EmployeeSelect({ value, name, onChange, disabled = false, companyId }: any) {
  const [options, setOptions] = useState<Array<{ id: string; name: string }>>([]);
  const [loading, setLoading] = useState(false);
  const searchRef = React.useRef<number | null>(null);

  const load = async (search?: string) => {
    setLoading(true);
    try {
      const resp = await getEmployees(search || undefined, companyId || undefined);
      const payload = Array.isArray(resp) ? resp : (resp?.data?.results || resp?.data || []);
      const mapped = payload.map((emp: any) => ({ id: emp.id || emp.value, name: emp.name || emp.nickname || emp.user?.full_name || emp.full_name || emp.label || '' }));
      setOptions(mapped);
    } catch (_err) {
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    void load('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  return (
    <AddButtonAutocomplete
      options={options}
      value={options.find((o) => o.id === value) || (value ? { id: value, name: name || value } : null)}
      onChange={(_e, newVal) => {
        const v = newVal as any;
        onChange(v ? v.id : null, v ? v.name : null);
      }}
      onInputChange={(_e, v, reason) => {
        if (reason === 'input' || reason === 'clear') {
          if (searchRef.current) window.clearTimeout(searchRef.current);
          searchRef.current = window.setTimeout(() => void load(v), 250) as unknown as number;
        }
      }}
      placeholder='Chọn hoặc tìm nhân viên'
      noOptionsText='Không tìm thấy nhân viên'
      loading={loading}
      disabled={disabled}
      fullWidth
    />
  );
}

function MaterialRequestSelect({ value, name, onChange, disabled = false, companyId }: any) {
  const [options, setOptions] = useState<Array<{ id: string; name: string }>>([]);
  const [loading, setLoading] = useState(false);
  const searchRef = React.useRef<number | null>(null);

  const load = async (search?: string) => {
    setLoading(true);
    try {
      const resp = await getMaterialRequests({ companyId: companyId || undefined, search: search || undefined, pageSize: 50 });
      const payload = resp?.results || [];
      const mapped = payload.map((r: any) => ({ id: r.id, name: r.request_no || r.id }));
      setOptions(mapped);
    } catch (_err) {
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    void load('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  return (
    <AddButtonAutocomplete
      options={options}
      value={options.find((o) => o.id === value) || (value ? { id: value, name: name || '' } : null)}
      onChange={(_e, newVal) => {
        const v = newVal as any;
        onChange(v ? v.id : null, v ? v.name : null);
      }}
      onInputChange={(_e, v, reason) => {
        if (reason === 'input' || reason === 'clear') {
          if (searchRef.current) window.clearTimeout(searchRef.current);
          searchRef.current = window.setTimeout(() => void load(v), 250) as unknown as number;
        }
      }}
      placeholder='Chọn phiếu đề xuất VT'
      noOptionsText='Không tìm thấy phiếu đề xuất'
      loading={loading}
      disabled={disabled}
      fullWidth
    />
  );
}

const SupplierAddModal: React.FC<AddOptionCustomDialogProps> = (props) => <ItemSupplierAddModal {...props} />;

const purchaseTypeOptions = [
  { id: 'reserve', name: 'Mua dự trữ' },
  { id: 'order', name: 'Mua theo đơn hàng' },
];

const formatDate = (value: string | null | undefined): string => {
  if (!value) return '—';
  const parsed = parseISO(value);
  return isValid(parsed) ? format(parsed, 'dd/MM/yyyy HH:mm') : value;
};

interface PurchaseOrderFormProps {
  formData: InventoryPurchaseOrderFormData;
  statusOptions: StatusManageItem[];
  supplierOptions: PurchasingSupplierOption[];
  supplierLoading?: boolean;
  onSupplierSearch: (search: string) => void;
  onCreateSupplier: (data: { name: string; email?: string; phone?: string; address?: any }) => Promise<PurchasingSupplierOption>;
  onChangeForm: (updates: Partial<InventoryPurchaseOrderFormData>) => void;
  onChangeLine: (lineId: string, updates: Partial<PurchaseOrderLine>) => void;
  onSave: () => void;
  canEdit?: boolean;
  isDirty?: boolean;
  saving?: boolean;
}

export default function PurchaseOrderForm({
  formData,
  statusOptions,
  supplierOptions,
  supplierLoading = false,
  onSupplierSearch,
  onCreateSupplier,
  onChangeForm,
  onChangeLine,
  onSave,
  canEdit = true,
  isDirty = false,
  saving = false,
}: PurchaseOrderFormProps) {
  const readOnly = !canEdit;
  const selectedSupplier = supplierOptions.find((s) => s.id === formData.supplier_id) || null;
  const [itemOptions, setItemOptions] = useState<RequestMaterialItemOption[]>([]);
  const isReservePurchase = formData.purchase_type === 'reserve';

  useEffect(() => {
    let mounted = true;
    const companyId = formData.company_id;

    if (!companyId) {
      setItemOptions([]);
      return () => {
        mounted = false;
      };
    }

    (async () => {
      try {
        const response = await getItems({ companyId, pageSize: 300 });
        if (!mounted) {
          return;
        }

        const mapped: RequestMaterialItemOption[] = (response.results || []).map((item) => ({
          id: item.id,
          name: item.name,
          code: item.code,
          unit_id: item.uom_id || undefined,
          unit_name: item.uom_name || undefined,
          supplier_id: item.supplier_id || undefined,
          supplier_name: item.supplier_name || undefined,
        }));
        setItemOptions(mapped);
      } catch (_error) {
        if (mounted) {
          setItemOptions([]);
        }
      }
    })();

    return () => {
      mounted = false;
    };
  }, [formData.company_id]);

  const effectiveStatusOptions = React.useMemo(() => {
    const normalizedOptions = (statusOptions || []).filter((option) => Boolean(option?.id));

    if (!formData.status) {
      return normalizedOptions;
    }

    const hasCurrentStatus = normalizedOptions.some((option) => option.id === formData.status);
    if (hasCurrentStatus) {
      return normalizedOptions;
    }

    return [
      ...normalizedOptions,
      {
        id: formData.status,
        name: formData.status_display || formData.status_code || formData.status,
        color: formData.status_color || '',
      } as StatusManageItem,
    ];
  }, [formData.status, formData.status_code, formData.status_display, formData.status_color, statusOptions]);

  // Ensure new POs get the default status once statusOptions are loaded
  useEffect(() => {
    if (!formData.status || formData.status === '') {
      const defaultStatus = statusOptions.find((o) => o.is_default);
      if (defaultStatus?.id) {
        onChangeForm({
          status: defaultStatus.id,
          status_display: defaultStatus.name,
          status_color: defaultStatus.color || '',
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusOptions, formData.status]);

  const hydratedMaterialRequestIdRef = React.useRef<string | null>(null);

  // When a material request is selected, load its items into the PO lines (once per MR id)
  useEffect(() => {
    let mounted = true;
    const mrId = formData.material_request_id;

    if (!mrId) {
      hydratedMaterialRequestIdRef.current = null;
      return;
    }

    if (hydratedMaterialRequestIdRef.current === mrId) {
      return;
    }

    hydratedMaterialRequestIdRef.current = mrId;

    (async () => {
      try {
        const resp = await getMaterialRequest(mrId);
        if (!mounted || !resp) return;

        const existingLines: PurchaseOrderLine[] = formData.lines || [];

        const mappedLines = (resp.lines || []).map((ln: any, idx: number) => {
          const base = {
            id: String(ln.id ?? `mr-line-${idx}`),
            sort_order: ln.sort_order ?? idx + 1,
            item_id: ln.item_id || (ln.item && ln.item.id) || '',
            item_code: ln.item_code || (ln.item && (ln.item.code || ln.item.item_code)) || '',
            item_name: ln.item_name || (ln.item && (ln.item.name || ln.item.item_name)) || '',
            uom_id: ln.uom_id || '',
            uom_name: ln.uom_name || (ln.uom && ln.uom.name) || '',
            requested_qty: ln.requested_qty != null ? String(ln.requested_qty) : '0',
            ordered_qty: ln.requested_qty != null ? String(ln.requested_qty) : '0',
            received_qty: '0',
            remaining_qty: ln.requested_qty != null ? String(ln.requested_qty) : '0',
            unit_price: ln.unit_price ?? ln.unitPrice ?? ln.price ?? '',
            line_amount: ln.line_amount ?? ln.lineAmount ?? ln.amount ?? '',
            manufacturer_id: null,
            manufacturer_name: null,
            source_request_line_id: ln.id != null ? String(ln.id) : null,
            note: '',
          } as PurchaseOrderLine;

          const matched = existingLines.find((ex) => (
            (ex.source_request_line_id && ln.id && ex.source_request_line_id === String(ln.id)) ||
            (ex.item_id && ln.item_id && ex.item_id === ln.item_id)
          ));

          if (matched) {
            if (matched.unit_price) base.unit_price = matched.unit_price;
            if (matched.line_amount) base.line_amount = matched.line_amount;
            if (matched.note) base.note = matched.note;
            if (matched.ordered_qty) base.ordered_qty = matched.ordered_qty;
          }

          return base;
        });

        onChangeForm({ lines: mappedLines });
      } catch (_err) {
        if (hydratedMaterialRequestIdRef.current === mrId) {
          hydratedMaterialRequestIdRef.current = null;
        }
      }
    })();

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.material_request_id]);
  const handleAddItem = () => {
    const existingLines = formData.lines || [];
    const maxSortOrder = existingLines.reduce((max, line) => Math.max(max, Number(line.sort_order || 0)), 0);
    const nextLine: PurchaseOrderLine = {
      id: `tmp-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
      sort_order: maxSortOrder + 1,
      item_id: '',
      item_code: '',
      item_name: '',
      uom_id: '',
      uom_name: '',
      requested_qty: '0',
      ordered_qty: '0',
      received_qty: '0',
      remaining_qty: '0',
      unit_price: '0',
      line_amount: '0',
      manufacturer_id: null,
      manufacturer_name: null,
      source_request_line_id: null,
      note: '',
    };

    onChangeForm({ lines: [...existingLines, nextLine] });
  };

  const handleCreateItem = (createdItem: RequestMaterialItemOption) => {
    setItemOptions((current) => {
      if (current.some((item) => item.id === createdItem.id)) {
        return current;
      }
      return [createdItem, ...current];
    });
  };

  return (
    <Stack spacing={4} sx={{ pt: 1, pb: 3 }}>
      <Stack spacing={3} sx={{ pb: 1 }}>
        <Grid container rowSpacing={2} columnSpacing={4}>

          {/* Mã PO – read-only */}
          <Grid size={{ xs: 12, md: 6 }}>
            <LabeledField label='Mã PO' labelWidth={140}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#1e293b', fontFamily: '"Roboto Mono", monospace' }}>
                  {formData.po_no || '—'}
                </Typography>
                {formData.status && (() => {
                  const currentStatus = statusOptions.find((o) => o.id === formData.status);
                  const label = currentStatus?.name || formData.status_display || formData.status;
                  const color = currentStatus?.color || formData.status_color || '';
                  return (
                    <Chip
                      size='small'
                      label={label}
                      sx={color ? { backgroundColor: color, color: '#fff', fontWeight: 500 } : undefined}
                    />
                  );
                })()}
              </Box>
            </LabeledField>
          </Grid>

          {/* Loại mua */}
          <Grid size={{ xs: 12, md: 6 }}>
            <LabeledField label='Loại mua' labelWidth={140}>
              <TextField
                select
                size='small'
                value={formData.purchase_type}
                disabled={readOnly}
                onChange={(e) => {
                  const nextPurchaseType = e.target.value;
                  if (nextPurchaseType === 'reserve') {
                    onChangeForm({
                      purchase_type: nextPurchaseType,
                      material_request_id: null,
                      material_request_no: null,
                    });
                    return;
                  }

                  onChangeForm({ purchase_type: nextPurchaseType });
                }}
                fullWidth
              >
                {purchaseTypeOptions.map((o) => (
                  <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>
                ))}
              </TextField>
            </LabeledField>
          </Grid>

          {/* Kho nhận */}
          <Grid size={{ xs: 12, md: 6 }}>
            <LabeledField label='Kho nhận' labelWidth={140}>
              {/* Use the same warehouse hook as RequestMaterialForm to load warehouses for the selected company */}
              <WarehouseSelect
                companyId={formData.company_id}
                value={formData.warehouse_id}
                warehouseName={formData.warehouse_name}
                disabled={readOnly}
                onChange={(warehouseId: string | null, warehouseName?: string) => onChangeForm({ warehouse_id: warehouseId || '', warehouse_name: warehouseName || '' })}
              />
            </LabeledField>
          </Grid>

          {/* Nhà cung cấp */}
          <Grid size={{ xs: 12, md: 6 }}>
            <LabeledField label='Nhà cung cấp' labelWidth={140}>
              <AddButtonAutocomplete
                options={supplierOptions}
                value={selectedSupplier}
                onChange={(_event, newValue) => {
                  const s = newValue as PurchasingSupplierOption | null;
                  onChangeForm({ supplier_id: s?.id || '', supplier_name: s?.name || '' });
                }}
                onInputChange={(_event, value, reason) => {
                  if (reason === 'input' || reason === 'clear') onSupplierSearch(value);
                }}
                loading={supplierLoading}
                filterOptions={(options) => options}
                placeholder='Chọn nhà cung cấp'
                noOptionsText='Không tìm thấy nhà cung cấp'
                disabled={readOnly}
                canAddNew={!readOnly}
                CustomDialog={SupplierAddModal}
                onAddNewCustom={async (data) => {
                  if (readOnly) {
                    return selectedSupplier || { id: '', name: '' };
                  }
                  const created = await onCreateSupplier(data);
                  onChangeForm({ supplier_id: created.id, supplier_name: created.name });
                  return created;
                }}
                fullWidth
              />
            </LabeledField>
          </Grid>

          {/* Trạng thái */}
          <Grid size={{ xs: 12, md: 6 }}>
            <LabeledField label='Trạng thái' labelWidth={140}>
              <TextField
                select
                size='small'
                value={formData.status || ''}
                onChange={(e) => {
                  const nextStatus = e.target.value;
                  const selectedStatus = effectiveStatusOptions.find((option) => option.id === nextStatus);
                  onChangeForm({
                    status: nextStatus,
                    status_display: selectedStatus?.name || '',
                    status_color: selectedStatus?.color || '',
                  });
                }}
                fullWidth
                SelectProps={{ displayEmpty: true }}
              >
                {effectiveStatusOptions.length === 0 && <MenuItem value=''><em>-- Chưa có trạng thái --</em></MenuItem>}
                {effectiveStatusOptions.map((o) => (
                  <MenuItem key={o.id} value={o.id ?? ''} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {o.color && (
                      <Box component='span' sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: o.color, flexShrink: 0, display: 'inline-block' }} />
                    )}
                    {o.name}
                  </MenuItem>
                ))}
              </TextField>
            </LabeledField>
          </Grid>

          {/* Người đề xuất */}
          <Grid size={{ xs: 12, md: 6 }}>
            <LabeledField label='Người đề xuất' labelWidth={140}>
              <EmployeeSelect
                value={formData.requested_by_id || ''}
                name={formData.requested_by_name || ''}
                companyId={formData.company_id}
                onChange={(id: string | null, name?: string | null) => onChangeForm({ requested_by_id: id || null, requested_by_name: name || null })}
                disabled={readOnly}
              />
            </LabeledField>
          </Grid>

          {/* Phiếu đề xuất VT */}
          {!isReservePurchase && (
            <Grid size={{ xs: 12, md: 6 }}>
              <LabeledField label='Phiếu ĐXVT' labelWidth={140}>
                <MaterialRequestSelect
                  value={formData.material_request_id || ''}
                  name={formData.material_request_no || ''}
                  companyId={formData.company_id}
                  onChange={(id: string | null, name?: string | null) => onChangeForm({ material_request_id: id || null, material_request_no: name || null })}
                  disabled={readOnly}
                />
              </LabeledField>
            </Grid>
          )}

          {/* Đơn hàng – read-only reference */}
          {formData.order_id && (
            <Grid size={{ xs: 12, md: 6 }}>
              <LabeledField label='Đơn hàng' labelWidth={140}>
                <Typography sx={{ fontSize: '0.875rem', color: '#2563eb', fontWeight: 500, py: 0.625, fontFamily: '"Roboto Mono", monospace' }}>
                  {formData.order_no || formData.order_id}
                </Typography>
              </LabeledField>
            </Grid>
          )}

          {/* Người duyệt – read-only (system-set) */}
          {formData.approved_by_name && (
            <Grid size={{ xs: 12, md: 6 }}>
              <LabeledField label='Người duyệt' labelWidth={140}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                  <Typography sx={{ fontSize: '0.875rem', color: '#1e293b' }}>
                    {formData.approved_by_name}
                  </Typography>
                  {formData.approved_at && (
                    <Typography sx={{ fontSize: '0.75rem', color: '#64748b' }}>
                      ({formatDate(formData.approved_at)})
                    </Typography>
                  )}
                </Box>
              </LabeledField>
            </Grid>
          )}

        </Grid>
      </Stack>

      <Box sx={{ pt: 1 }}>
        <PurchaseOrderLinesTable
          lines={formData.lines ?? []}
          onChangeLine={onChangeLine}
          onAddItem={handleAddItem}
          itemOptions={itemOptions}
          onCreateItem={handleCreateItem}
          companyId={formData.company_id}
          companyName={formData.company_name}
          canEdit={canEdit}
        />
      </Box>

      <Box sx={{ pt: 1, width: '100%' }}>
        <TextareaAutosize
          minRows={4}
          value={formData.note}
          disabled={readOnly}
          onChange={(e) => onChangeForm({ note: e.target.value })}
          placeholder='Nhập ghi chú cho phiếu mua...'
          style={{
            width: '100%',
            borderRadius: '6px',
            border: '1px solid #cbd5e1',
            padding: '16px 18px',
            fontSize: '0.95rem',
            lineHeight: 1.6,
            resize: 'vertical',
            outline: 'none',
            fontFamily: 'inherit',
            backgroundColor: '#fff',
          }}
        />
      </Box>

      <Box sx={{ pt: 2, width: '100%' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent='flex-end' alignItems={{ xs: 'stretch', sm: 'center' }} spacing={2}>
          <Button variant='contained' onClick={onSave} disabled={saving || !isDirty || readOnly} size='small'>
            {saving ? 'Đang lưu...' : 'Lưu phiếu mua'}
          </Button>
        </Stack>
      </Box>
    </Stack>
  );
}
